import { inject, observer } from 'mobx-react';
import React, { Component, Fragment } from 'react';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Alert, Card, CardBody, Col, Container, Row } from 'reactstrap';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';

import { modelOf } from '../../prop-types';
import Paths from '../../types/Paths';
import RouteService from '../../services/RouteService';
import CanonicalLink from '../../components/head/CanonicalLink';
import AccountStore from '../../store/AccountStore';
import Page from '../../components/layout/Page';
import ScrollReset from '../../components/common/ScrollReset';
import HelperNavigation from '../../components/common/HelperNavigation';
import globalTranslations from '../../i18n/globalTranslations';
import PageTitle from '../../components/common/PageTitle';
import { getStorageAndParse, stringifyAndSetStorage } from '../../util/storage';
import { storageKeys } from '../../util/constants';
import LastVisitedProductsSlider from '../../components/slider/LastVisitedProductsSlider';
import ProductStore from '../../store/ProductStore';
import ConfigStore from '../../store/ConfigStore';
import AccountModalTab from '../../types/AccountModalTab';
import ButtonLink from '../../components/common/ButtonLink';
import UIStore from '../../store/UIStore';
import WishListEmailForm from '../../components/wish-list/WishListEmailForm';
import WishListProductRow from '../../components/wish-list/WishListProductRow';
import SEOTitle from '../../components/head/SEOTitle';
import ProductClass from '../../types/ProductClass';
import CommonPage from '../../types/CommonPage';

const MobileWishlistLabels = {
  name: <FormattedMessage id="wishlist.Product" defaultMessage="Product" />,
  quantity: (
    <FormattedMessage
      id="wishlist.AmountAndInTotal"
      defaultMessage="Amount / In Total"
    />
  ),
  unitPrice: (
    <FormattedMessage id="wishlist.UnitPrice" defaultMessage="Price per Unit" />
  )
};

@observer
class WishListPage extends Component {
  constructor(props) {
    super(props);
    this.redirectIfWishlistIsNotEnabled();
  }

  state = {
    storage: [],
    notFound: false,
    alertVisible: true
  };

  redirectIfWishlistIsNotEnabled = () => {
    const { configStore, routeService, history } = this.props;
    if (!configStore.wishlist.enabled) {
      history.push(routeService.getPath(Paths.NotFoundPage));
    }
  };

  componentDidMount() {
    const { accessToken } = this.props.match.params;
    let localProductList = getStorageAndParse(storageKeys.wishlist);
    const { accountStore, productStore } = this.props;

    if (accessToken) {
      productStore
        .getProductByAccessToken({ accessToken })
        .then((response) => {
          const productList = this.getSavedProductList(
            response.data,
            localProductList
          );
          this.setState({ storage: productList });
        })
        .catch(() => {
          this.setState({ notFound: true });
        });
    } else if (accountStore.loggedIn) {
      this.fetchProductList();
    } else {
      this.setState({ storage: localProductList });
    }
  }

  fetchProductList = () => {
    const { productStore } = this.props;
    productStore
      .getProductList()
      .then((response) => {
        this.setState({ storage: response.data });
      })
      .catch((e) => {
        console.error(e);
      });
  };

  getSavedProductList = (productList, localProductList) => {
    productList.forEach((product) => {
      localProductList = localProductList.filter(
        (val) => val.product_id !== product.product_id
      );
    });
    return productList.concat(localProductList);
  };

  renderWishListContent = () => (
    <Fragment>
      <Row className="WishListPage__card-row">
        <Col xs={12} xl={9}>
          <Card className="WishListPage__product-list-card">
            <CardBody className="WishListPage__product-list-card-body">
              {this.notLoggedInAlertBanner()}
              {this.productListInformationAlertBanner()}
              {this.productListNotFoundAlertBanner()}
              {this.emptyProductList()}
              {this.productInfoLabelRow()}
              {this.renderWishList()}
            </CardBody>
          </Card>
        </Col>
        <Col xs={12} xl={3}>
          <Card className="WishListPage__product-list-card">
            <CardBody className="WishListPage__product-list-card-body-email-form">
              {this.emailWishlistForm()}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Fragment>
  );

  getBreadcrumbsPath = () => {
    const { intl, routeService } = this.props;
    return routeService.transformBreadCrumbs([
      {
        text: intl.formatMessage(globalTranslations.wishListTitle),
        url: Paths.WishList
      }
    ]);
  };

  renderProductHeaderCol = (label, sizes) => {
    const actualSizes = Object.assign(
      {
        xs: 4
      },
      sizes
    );
    return (
      <Col className="WishListPage__product-list-header" {...actualSizes}>
        <label className="WishListPage__product-list-header-label">
          {label}
        </label>
      </Col>
    );
  };

  productListNotFoundAlertBanner = () => {
    if (!this.state.notFound) {
      return null;
    }
    const message = (
      <FormattedMessage
        id="wishlist.wishlistNotFound"
        defaultMessage="Wishlist doesn't exist with the given id."
      />
    );
    return this.renderAlertBanner('', message, 'warning');
  };

  emptyProductList = () => {
    if (this.state.storage.length || this.props.match.params.accessToken) {
      return null;
    }
    const message = (
      <FormattedMessage
        id="wishlist.wishlistIsEmpty"
        defaultMessage="Wishlist is empty."
      />
    );
    return this.renderAlertBanner('', message, 'secondary');
  };

  productInfoLabelRow = () => {
    const { uiStore } = this.props;
    if (!uiStore.isDesktop || !this.state.storage.length) {
      return null;
    }
    return (
      <Row className="WishListPage__product-list--headers">
        {this.renderProductHeaderCol(MobileWishlistLabels.name, {
          md: 4
        })}
        {this.renderProductHeaderCol(MobileWishlistLabels.unitPrice, {
          sm: 2
        })}
        {this.renderProductHeaderCol(MobileWishlistLabels.quantity, {
          sm: 2,
          md: 4
        })}
      </Row>
    );
  };

  updateStorage = (updatedProduct) => {
    const localProductList = getStorageAndParse(storageKeys.wishlist);
    const productIndex = localProductList.findIndex(
      (p) => p.id === updatedProduct.id
    );
    localProductList.splice(productIndex, 1, updatedProduct);
    stringifyAndSetStorage(storageKeys.wishlist, localProductList);
    this.setState({ storage: localProductList });
  };

  deleteProduct = (product) => {
    if (this.props.accountStore.loggedIn) {
      this.fetchProductList();
    } else {
      this.deleteLocalProduct(product);
    }
  };

  deleteLocalProduct(product) {
    const productId = product.id;
    const localProductList = getStorageAndParse(storageKeys.wishlist);
    const productIndex = localProductList.findIndex(
      (ProductlistProduct) => ProductlistProduct.id === productId
    );
    localProductList.splice(productIndex, 1);
    stringifyAndSetStorage(storageKeys.wishlist, localProductList);
    this.setState({ storage: localProductList });
  }

  renderWishList = () => {
    const { storage } = this.state;
    const { match } = this.props;
    if (storage) {
      return storage.map((product) => (
        <Fragment key={this.getProductIdByProductClass(product)}>
          <div className="WishListPage_WishlistProductRow-divider" />
          <WishListProductRow
            product={product}
            match={match}
            onStorageUpdate={this.updateStorage}
            onProductDelete={this.deleteProduct}
          />
        </Fragment>
      ));
    }
  };

  getProductIdByProductClass = (product) => {
    return product.class === ProductClass.MULTI ||
      product.class === ProductClass.COLLECTION
      ? product.id
      : product.product_id;
  };

  accountLoginButtonLink = () => (
    <ButtonLink onClick={this.toggle.bind(this, AccountModalTab.LOGIN)}>
      <u>
        <FormattedMessage id="wishlist.login" defaultMessage="login" />
      </u>
    </ButtonLink>
  );

  notLoggedInAlertBanner = () => {
    const { accountStore, configStore } = this.props;
    if (accountStore.loggedIn || !this.state.storage.length) {
      return null;
    }

    if (configStore.registration.enabled && !accountStore.loggedIn) {
      return this.alertWithRegistrationLink();
    } else {
      const message = (
        <FormattedMessage
          id="wishlist.NotLoggedInBanner"
          defaultMessage="You're not yet logged in. You have to {login_link} as a customer to send the list as an email or save it permanently."
          values={{
            login_link: (
              <ButtonLink
                onClick={this.toggle.bind(this, AccountModalTab.LOGIN)}
              >
                <u>
                  <FormattedMessage
                    id="wishlist.login"
                    defaultMessage="login"
                  />
                </u>
              </ButtonLink>
            )
          }}
        />
      );
      return this.renderAlertBanner('', message, 'warning', true);
    }
  };

  alertWithRegistrationLink = () => {
    const message = (
      <FormattedMessage
        id="wishlist.NotLoggedInBanner_Registration_Enabled"
        defaultMessage={`
          You're not yet logged in. You have to {login_link} or {register_link} as a customer to send the list as an email or save it permanently.,
          `}
        values={{
          login_link: this.accountLoginButtonLink(),
          register_link: (
            <ButtonLink
              onClick={this.toggle.bind(this, AccountModalTab.REGISTER)}
            >
              <u>
                <FormattedMessage
                  id="wishlist.register"
                  defaultMessage="register"
                />
              </u>
            </ButtonLink>
          )
        }}
      />
    );
    return this.renderAlertBanner('', message, 'warning', true);
  };

  onAlertDismiss = () => {
    this.setState({
      alertVisible: false
    });
  };

  toggle = (tab) => {
    const { accountStore } = this.props;
    accountStore.accountModal.setTab(tab);
    accountStore.accountModal.toggle();
  };

  emailWishlistForm = () => {
    const { accountStore, configStore, match } = this.props;
    if (
      !accountStore.loggedIn ||
      !configStore.wishlist.enableEmailShare ||
      this.state.notFound ||
      match.params.accessToken
    ) {
      return null;
    }

    if (this.state.storage.length) {
      return <WishListEmailForm />;
    }
  };

  productListInformationAlertBanner = () => {
    const { accountStore, match } = this.props;
    const { storage, notFound } = this.state;
    if (
      !accountStore.loggedIn ||
      !storage.length ||
      notFound ||
      match.params.accessToken
    ) {
      return null;
    }

    const message = (
      <FormattedMessage
        id="wishlist.ProductListEmailInformation"
        defaultMessage="You can add products to the list and make order decision later. You can also send the list as an email of your choosing."
      />
    );
    return this.renderAlertBanner(
      'WishListPage__product-list-information-alert-banner',
      message,
      'secondary'
    );
  };

  renderAlertBanner = (className, formattedMessage, color, dismissable) => (
    <Alert
      className={classNames(className)}
      color={color}
      isOpen={this.state.alertVisible}
      toggle={dismissable && this.onAlertDismiss}
    >
      {formattedMessage}
    </Alert>
  );

  render() {
    return (
      <Page name={CommonPage.WISH_LIST} className="WishListPage">
        <SEOTitle title={globalTranslations.wishListTitle} />
        <ScrollReset />
        <Container className="WishListPage__container">
          <Row>
            <Col xs={12}>
              <CanonicalLink
                path={this.props.routeService.getPath(Paths.WishList)}
              />
              <div className="WishListPage__HelperNavigation">
                <HelperNavigation breadcrumbsPath={this.getBreadcrumbsPath()} />
              </div>
              <PageTitle>
                <FormattedMessage {...globalTranslations.wishListTitle} />
              </PageTitle>
            </Col>
          </Row>
          {this.renderWishListContent()}
          <LastVisitedProductsSlider />
        </Container>
      </Page>
    );
  }
}

WishListPage.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  productStore: modelOf(ProductStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  intl: intlShape.isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired
};

export default injectIntl(
  withRouter(
    inject(
      'accountStore',
      'routeService',
      'productStore',
      'configStore',
      'uiStore'
    )(WishListPage)
  )
);



// WEBPACK FOOTER //
// ./src/pages/WishListPage/index.jsx